import React, { Component } from 'react';

import Animator from '../Animator';
import { TX, LinearGradient, mk_svg_id_and_url, wedge_calc } from '../SVGUtil';
import MUTIL from '../MUTIL';
import { coalesce } from '../misc';

function Wedge(props) {
	const { radius, zero_angle, fraction, offset } = props;

	const start_angle = zero_angle + offset*360;

	let w = wedge_calc(fraction, radius, start_angle);

	if (w.circle) {
		return (
			<g opacity={props.opacity}>
				<circle r={w.circle.r} fill={props.face_fill} filter="url(#wedge)"/>
				<circle r={w.circle.r} fill="none" stroke={props.edge_fill} strokeWidth={1.5} strokeLinejoin="round"/>
			</g>
		);
	} else if (w.path) {
		return (
			<g opacity={props.opacity}>
				<path d={w.path.d} fill={props.face_fill} filter="url(#wedge)"/>
				<path d={w.path.d} fill="none" stroke={props.edge_fill} strokeWidth={1.5} strokeLinejoin="round"/>
			</g>
		);
	}
}

function PieHoleWedges(props) {

	const size = props.size || 256;
	const padding = coalesce(props.padding, 16);
	const diameter = size - padding*2;
	const radius = diameter/2;
	const zero_angle = coalesce(props.zero_angle, 90);

	const colors = props.colors.map(MUTIL.val2col);

	var gradients = [];
	var face_color_urls = [];
	var edge_color_urls = [];
	var i;
	for (i = 0; i < colors.length; i++) {
		const [face_id, face_url] = mk_svg_id_and_url();
		const [edge_id, edge_url] = mk_svg_id_and_url();
		const s = 1;
		const x1 = -radius * s;
		const y1 = -radius * s;
		const x2 = radius * s;
		const y2 = radius * s;

		const face_stops = [
			[0, MUTIL.col_lighten(colors[i], 0)],
			[1, MUTIL.col_lighten(colors[i], 0)],
		];
		gradients.push(
			<LinearGradient
				key={face_id}
				id={face_id}
				x1={x1} y1={y1} x2={x2} y2={y2}
				stops={face_stops}
			/>
		);
		face_color_urls.push(face_url);

		const edge_opacity = 0.2;
		const edge_stops = [
			[0, MUTIL.col_lighten(colors[i], 0), edge_opacity],
			[1, MUTIL.col_lighten(colors[i], 0), edge_opacity],
		];
		gradients.push(
			<LinearGradient
				key={edge_id}
				id={edge_id}
				x1={x1} y1={y1} x2={x2} y2={y2}
				stops={edge_stops}
			/>
		);
		edge_color_urls.push(edge_url);
	}

	var wedges = [];

	const ulist = props.ulist;
	if (props.fractions.length === 1) {
		const u = ulist[0];
		if (u !== 0) {
			wedges = [
				<Wedge
					key="one"
					opacity={1}
					zero_angle={zero_angle}
					radius={radius}
					face_fill={face_color_urls[0]}
					edge_fill={edge_color_urls[0]}
					fraction={u}
					offset={0}
				/>
			];
		}
	} else {
		const N = props.fractions.length;
		var calculated_offsets = [];
		for (i = 0; i < N; i++) {
			const u = ulist[i];
			var offset;
			if (i === 0) {
				offset = props.offsets[i];
			} else {
				const d = calculated_offsets[i-1] + props.fractions[i-1];
				offset = MUTIL.lerp(u, d - props.fractions[i], d);
			}
			calculated_offsets.push(offset);
		}

		for (i = N-1; i >= 0; i--) {
			const fraction = props.fractions[i];
			const color_index = i % colors.length;
			const wedge_radius = MUTIL.lerpn(i, N, radius, radius - 20) * ulist[0];
			const u = ulist[i];
			const opacity = MUTIL.clamp(u * 2, 0, 1);
			const offset = calculated_offsets[i];
			if (u === 0) continue;
			wedges.push(
				<Wedge
					key={i}
					opacity={opacity}
					zero_angle={zero_angle}
					radius={wedge_radius}
					face_fill={face_color_urls[color_index]}
					edge_fill={edge_color_urls[color_index]}
					fraction={fraction}
					offset={offset}
				/>);
		}
	}

	return (
		<svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
			{gradients}
			<g transform={TX.translate(size/2,size/2).str}>
				{wedges}
			</g>
		</svg>
	);
}

function PieHoleBullet(props) {
	const size = props.size || 20;
	const padding = props.padding || 5;
	const r = size/2 - padding;

	const face_color = MUTIL.val2hex(props.color);
	const edge_color = MUTIL.col2hex(MUTIL.col_lighten(MUTIL.val2col(props.color), -0.2));

	return (
		<svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
			<circle
				r={r}
				cx={size/2}
				cy={size/2}
				fill={face_color}
				filter="url(#shadow)"
			/>
			<circle
				r={r}
				cx={size/2}
				cy={size/2}
				fill="none"
				stroke={edge_color}
				strokeWidth={0.5}
			/>
		</svg>
	);
}

function PieHoleLegend(props) {
	const N = props.fractions.length;
	var rows = [];

	if (props.header) {
		rows.push(
			<tr key="header" style={{opacity: props.ulist[0]}}>
				<td colSpan="3">{props.header}</td>
			</tr>
		);
	}

	for (var i = 0; i < N; i++) {
		const fraction = props.fractions[i];
		const pct = Math.round(fraction*100);
		const title = props.titles[i];
		const color = props.colors[i%props.colors.length];
		rows.push(
			<tr key={i} style={{opacity: props.ulist[i]}}>
				<td className="pr-2"><PieHoleBullet color={color}/></td>
				<td>{title}</td>
				<td className="pl-4">{pct}%</td>
			</tr>
		);
	}
	return (
		<React.Fragment>
			<table><tbody>{rows}</tbody></table>
		</React.Fragment>
	);
}

class PieHoleAnim extends Component {
	constructor(props) {
		super(props);
		this.state = { u: 0 };
	}

	componentDidMount() {
		this.animator = new Animator({
			speed: 1/this.props.total_duration,
			callback: (u) => this.setState({u})
		});
	}

	componentWillUnmount() {
		if (this.animator) this.animator.kill();
	}

	componentDidUpdate(prev_props) {
		if (!this.animator) return;
		if (prev_props.seq !== this.props.seq) this.animator.reset();
	}

	render() {
		const props = this.props;

		const t = this.state.u * props.total_duration;

		const N = props.fractions.length;
		var ulist = [];
		for (var i = 0; i < N; i++) {
			var u;
			if (i === 0) {
				u = MUTIL.unlerp(t, props.first_wedge_delay, props.first_wedge_delay + props.first_wedge_duration);
			} else {
				const off = props.first_wedge_delay + props.second_wedge_delay + (i-1) * props.wedge_interval;
				u = MUTIL.unlerp(t, off, off + props.wedge_duration);
			}
			u = MUTIL.clamp(u, 0, 1);
			if (i === 0) {
				if (N === 1) {
					u = MUTIL.ease(MUTIL.clamp(
						MUTIL.unlerp(t, props.first_wedge_delay, props.total_duration),
						0,
						1
					));
				} else {
					u = MUTIL.cubic_bezier(u, 0, 1.0, 1.1, 1);
				}
			} else {
				u = MUTIL.ease(u);
			}
			ulist.push(u);
		}

		const container_class = coalesce(props.container_class, "d-flex");
		const wedges_class = coalesce(props.wedges_class, "d-flex align-items-center");
		const legend_class = coalesce(props.legend_class, "d-flex align-items-center pl-5");

		return (
			<div className={container_class}>
				<div className={wedges_class}>
					<PieHoleWedges
						size={props.pie_size}
						padding={props.pie_padding}
						colors={props.colors}
						fractions={props.fractions}
						offsets={props.offsets}
						ulist={ulist}
					/>
				</div>
				<div className={legend_class}>
					<PieHoleLegend
						header={props.legend_header}
						colors={props.colors}
						fractions={props.fractions}
						titles={props.titles}
						ulist={ulist}
					/>
				</div>
			</div>
		);
	}
}

function PieHole(props) {
	var values = props.values;

	if (coalesce(props.sort, true)) {
		values = [...values].sort((a,b) => b[0]-a[0]);
	}

	const skip_zero_percent = coalesce(props.skip_zero_percent, true);

	var sum = 0;
	for (const v of values) sum += v[0];

	var fractions = [];
	var offsets = [];
	var titles = [];

	var offset = 0;
	for (const v of values) {
		const fraction = v[0]/sum;
		if (skip_zero_percent && Math.round(fraction * 100) === 0) continue;
		fractions.push(fraction);
		offsets.push(offset);
		offset += fraction;
		titles.push(v[1]);
	}

	var colors = props.colors;
	if (!colors) {
		colors = [
			"#019852",
			"#0177FF",
			"#ED3A3A",
			"#FFA500",
			"#FB677F",
			"#EEEEEE",
		];
	}

	const first_wedge_delay = coalesce(props.first_wedge_delay, 1.0);
	const first_wedge_duration = coalesce(props.first_wedge_delay, 0.3);
	const second_wedge_delay = coalesce(props.first_wedge_delay, 0.3);
	const wedge_interval = coalesce(props.first_wedge_delay, 0.15);
	const wedge_duration = coalesce(props.first_wedge_delay, 0.4);

	const N = values.length;
	const total_duration = Math.max(
		first_wedge_delay + first_wedge_duration,
		first_wedge_delay + second_wedge_delay + wedge_interval*(N-1) + wedge_duration
	);

	var iprops = {
		...props,
		fractions,
		offsets,
		titles,
		colors,
		first_wedge_delay,
		first_wedge_duration,
		second_wedge_delay,
		wedge_interval,
		wedge_duration,
		total_duration,
	};
	delete iprops.values;
	delete iprops.sort;
	return <PieHoleAnim {...iprops}/>;
}

export default PieHole;



// WEBPACK FOOTER //
// ./src/c/PieHole.js